import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import FileItemUpload from '../UploadAvatarImage/FileItemUpload';
import Text from 'components/Text';
import Box from 'components/Box';
import colorTheme from 'constants/color';
import IcUpload from 'assets/Icons/IcUpload';

export interface UploadQRImageProps {
  value?: string | null;
  onChange?: (val: File) => void;
  style?: React.CSSProperties;
  className?: string;
  onDelete?: () => void;
  showQR?: boolean;
}

const UploadQRImage = ({ value, onChange, style, className, onDelete, showQR }: UploadQRImageProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = (files: FileList | null) => {
    if (!files || !files.length) return;

    const file = files[0];
    const fileRename = new File([file], file.name, {
      type: file.type,
    });

    onChange?.(fileRename);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const handleDelete = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }

    onDelete?.();
  };

  return (
    <Container
      isDragging={isDragging}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
      }}
      onDrop={handleDrop}
      style={style}
      className={className}
    >
      <HiddenInput ref={inputRef} type='file' accept='image/*' onChange={handleSelect} />
      {!value ? (
        <Placeholder isDragging={isDragging}>
          <IcUpload />
          <Text variant='CONTENT_1' color='text_3_v2' textAlign='center'>
            No image uploaded
          </Text>
        </Placeholder>
      ) : (
        <Box display='flex' justifyContent='center' alignItems='center' p='3' bgColor='v2_neutral_2' borderRadius='16px'>
          <FileItemUpload fileInfo={value} onDelete={handleDelete} borderRadius='0' showQR={showQR} />
        </Box>
      )}
    </Container>
  );
};

export default UploadQRImage;

const Container = styled.div<{ isDragging: boolean }>`
  width: 100%;
  border-radius: 24px;
  padding: 16px;
  background: #FFF;
  box-shadow: 0 4px 8px -4px rgba(0, 0, 0, 0.15);
`;

const HiddenInput = styled.input`
  display: none !important;
`;

const Placeholder = styled(Box)<{ isDragging: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 100%;
  border-radius: 24px;
  padding: 8px;

  border: 1px dashed ${({ isDragging }) => (isDragging ? colorTheme.error_3 : colorTheme.v2_border_3)};
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${colorTheme.v2_neutral_2};

  &: hover {
    border-color: ${colorTheme.hover_info};
  }
`;
